import React from "react";
import { useTranslation } from "react-i18next";
import { EshopAccessory } from "./checkoutOrderView";
import currencyFormatter, { Currency } from "@/common/CurrencyFormatter";

interface OfferEshopAccessoryItemProperties {
    readonly eshopAccessory: EshopAccessory;
    readonly price: number;
    readonly isSelected: boolean;
    readonly onChange: (eshopAccessory: EshopAccessory, isSelected: boolean) => void;
    readonly onPriceChange: (eshopAccessory: EshopAccessory, price: number) => void;
    readonly currency: Currency;
}

const priceIncreaseEmojis = [
    <img src="/css/image/courtesy-1-good.png" key={0} alt="" />,
    <img src="/css/image/courtesy-2-better.png" key={1} alt="" />,
    <img src="/css/image/courtesy-3-excellent.png" key={2} alt="" />,
    <img src="/css/image/courtesy-4-outstanding.png" key={3} alt="" />,
];

export default function OfferEshopAccessoryItem({
    eshopAccessory,
    price,
    isSelected,
    onChange,
    onPriceChange,
    currency,
}: OfferEshopAccessoryItemProperties): React.JSX.Element {
    const { t } = useTranslation("basket");
    const [isActive, setIsActive] = React.useState(isSelected);
    const [selectedPrice, setSelectedPrice] = React.useState(price);

    const handleChange = (event: React.MouseEvent): void => {
        event.preventDefault();
        setIsActive(!isActive);
        onChange(eshopAccessory, !isActive);
    };

    const handlePriceChange = (event: React.MouseEvent, increasedPrice: number): void => {
        event.preventDefault();
        event.stopPropagation();
        setSelectedPrice(increasedPrice);
        onPriceChange(eshopAccessory, increasedPrice);
    };

    const prices = [eshopAccessory.price, ...eshopAccessory.morePrices ?? []];

    const emojisPriceMap = new Map<number, React.JSX.Element>();

    for (const [index, price_] of prices.entries()) {
        emojisPriceMap.set(price_, priceIncreaseEmojis[Math.min(index, 3)]);
    }

    const itemPrices = prices.map((priceValue) => <button
        type="button"
        key={priceValue}
        className={priceValue === selectedPrice ? "active" : undefined}
        onClick={(event): void => handlePriceChange(event, priceValue)}
    >
        {currencyFormatter.formatCurrencyWithSymbol(priceValue, currency)}
    </button>);

    return (
        <li className={isActive ? "active" : undefined} onClick={handleChange}>
            <div className="accessory-title-wrap">
                <div className="accessory-title">
                    <h4>{eshopAccessory.title}</h4>
                </div>
                <div className="accessory-info">
                    <i className="fa-solid fa-circle-info"></i>
                </div>
            </div>
            <div className="accessory-description">
                <p>
                    {eshopAccessory.description !== null
                      && <span dangerouslySetInnerHTML={{ __html: eshopAccessory.description }}></span>}
                </p>
                {itemPrices.length > 1
                  && <>
                      <div className="accessory-buttons">{itemPrices}</div>
                      <div className="accessory-emoji">{emojisPriceMap.get(selectedPrice)}</div>
                  </>}
            </div>
            <div className="accessory-status-wrap">
                <div className="accessory-status">
                    <span className="accessory-radiobutton"></span>
                    <p>{t("added")}</p>
                </div>
                <div className="accessory-price">
                    <p>{currencyFormatter.formatCurrencyWithSymbol(selectedPrice, currency)}</p>
                </div>
            </div>
        </li>
    );
}
